import type { EnrichedTableField } from '../types/metadata';

// ─── Type group constants ────────────────────────────────────────────────────

export const NUMBER_TYPES = ['integer', 'numeric', 'bigint', 'real', 'double precision'] as const;
export const TEXT_TYPES   = ['text', 'character varying'] as const;
export const DATE_TYPES   = ['date', 'timestamp', 'timestamp with time zone', 'timestamp without time zone'] as const;
export const JSON_TYPES   = ['jsonb', 'json'] as const;

export type FieldCategory =
  | 'picklist'
  | 'relationship'
  | 'phone'
  | 'email'
  | 'file'
  | 'currency'
  | 'percentage'
  | 'numeric'
  | 'text'
  | 'date'
  | 'boolean'
  | 'uuid'
  | 'json'
  | 'unknown';

// ─── Boolean helpers ─────────────────────────────────────────────────────────

export const isNumberField     = (field: EnrichedTableField): boolean =>
  NUMBER_TYPES.includes(field.data_type as any) && !field.relationship;

export const isTextField       = (field: EnrichedTableField): boolean =>
  TEXT_TYPES.includes(field.data_type as any) && !(field as any).picklist;

export const isDateField       = (field: EnrichedTableField): boolean =>
  DATE_TYPES.includes(field.data_type as any);

export const isBooleanField    = (field: EnrichedTableField): boolean =>
  field.data_type === 'boolean';

export const isUUIDField       = (field: EnrichedTableField): boolean =>
  field.data_type === 'uuid';

export const isJsonField       = (field: EnrichedTableField): boolean =>
  JSON_TYPES.includes(field.data_type as any);

export const isPicklistField   = (field: EnrichedTableField): boolean =>
  !!(field as any).picklist;

export const isRelationshipField = (field: EnrichedTableField): boolean =>
  !!field.relationship;

export const isCurrencyField   = (field: EnrichedTableField): boolean =>
  field.ui_is_currency && isNumberField(field);

export const isFileField       = (field: EnrichedTableField): boolean =>
  !!(field as any).is_file;

export const isPercentageField = (field: EnrichedTableField): boolean =>
  field.ui_is_percentage && isNumberField(field);

// ─── Category ────────────────────────────────────────────────────────────────

export const getFieldCategory = (field: EnrichedTableField): FieldCategory => {
  if (isPicklistField(field))           return 'picklist';
  if (isRelationshipField(field))       return 'relationship';
  if (field.is_phone)                   return 'phone';
  if (field.is_email)                   return 'email';
  if (isFileField(field))               return 'file';
  if (isCurrencyField(field))           return 'currency';
  if (isPercentageField(field))         return 'percentage';
  if (isNumberField(field))             return 'numeric';
  if (isTextField(field))               return 'text';
  if (isDateField(field))               return 'date';
  if (isBooleanField(field))            return 'boolean';
  if (isUUIDField(field))               return 'uuid';
  if (isJsonField(field))               return 'json';
  return 'unknown';
};

// ─── Grouping ─────────────────────────────────────────────────────────────────

export interface GroupedFields {
  picklists:     EnrichedTableField[];
  relationships: EnrichedTableField[];
  phone:         EnrichedTableField[];
  email:         EnrichedTableField[];
  file:          EnrichedTableField[];
  currency:      EnrichedTableField[];
  percentage:    EnrichedTableField[];
  numeric:       EnrichedTableField[];
  text:          EnrichedTableField[];
  date:          EnrichedTableField[];
  boolean:       EnrichedTableField[];
  uuid:          EnrichedTableField[];
  json:          EnrichedTableField[];
  unknown:       EnrichedTableField[];
}

export const emptyGroupedFields = (): GroupedFields => ({
  picklists: [], relationships: [], phone: [], email: [], file: [],
  currency: [], percentage: [], numeric: [], text: [],
  date: [], boolean: [], uuid: [], json: [], unknown: [],
});

export const groupFieldsByCategory = (fields: EnrichedTableField[]): GroupedFields => {
  const grouped = emptyGroupedFields();

  for (const field of fields) {
    switch (getFieldCategory(field)) {
      case 'picklist':      grouped.picklists.push(field);     break;
      case 'relationship':  grouped.relationships.push(field); break;
      case 'phone':         grouped.phone.push(field);         break;
      case 'email':         grouped.email.push(field);         break;
      case 'file':          grouped.file.push(field);          break;
      case 'currency':      grouped.currency.push(field);      break;
      case 'percentage':    grouped.percentage.push(field);    break;
      case 'numeric':       grouped.numeric.push(field);       break;
      case 'text':          grouped.text.push(field);          break;
      case 'date':          grouped.date.push(field);          break;
      case 'boolean':       grouped.boolean.push(field);       break;
      case 'uuid':          grouped.uuid.push(field);          break;
      case 'json':          grouped.json.push(field);          break;
      default:
        console.warn(`Unknown field type: ${field.data_type} for field: ${field.name}`);
        grouped.unknown.push(field);
    }
  }

  return grouped;
};
